import React, { useEffect, useRef, useState } from 'react';
import { calculateSwatchBlockCount, recalculateSwatchLayout } from './SwatchLayout';
import { useLayoutEffect } from 'react';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import { useSelector } from 'react-redux';

const dragStartThreshold = 5;
const autoScrollEdge = 50;
const autoScrollMaxSpeed = 18;
let swatchBoardDebugIdSeed = 0;

const cardLabelStyle = {
    padding: '5px 7px',
    fontSize: 11,
    fontWeight: 400
};

const cardValueStyle = {
    padding: '5px 7px',
    fontSize: 11,
    fontWeight: 400,
    backgroundColor: '#eee',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
};

function removeObservedScrollListener(observerRef) {
    const observation = observerRef.current;
    if (!observation) return;
    observation.element.removeEventListener('scroll', observation.listener);
    observerRef.current = null;
}

function positiveNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
}

function sameItemOrder(left, right) {
    return left.length === right.length && left.every((item, index) => item.itemKey === right[index].itemKey);
}

function pageCount(items) {
    return items.reduce((maximum, item) => Math.max(maximum, positiveNumber(item.detail.SWT_PAGENO, 1)), 1);
}

function groupItemsByPage(items, minimumPageCount) {
    const pages = [];
    const maximumPage = Math.max(pageCount(items), minimumPageCount || 1);

    for (let pageNo = 1; pageNo <= maximumPage; pageNo += 1) {
        pages.push({ pageNo: pageNo, items: [] });
    }

    items.forEach(item => {
        const pageNo = positiveNumber(item.detail.SWT_PAGENO, 1);
        let page = pages.find(value => value.pageNo === pageNo);
        if (!page) {
            page = { pageNo: pageNo, items: [] };
            pages.push(page);
        }
        page.items.push(item);
    });

    return pages.sort((a, b) => a.pageNo - b.pageNo);
}

function recalculatePreviewItems(items, gataSu) {
    const details = recalculateSwatchLayout(items.map(item => item.detail), gataSu);
    return items.map((item, index) => ({
        ...item,
        detail: details[index]
    }));
}

function nearestVerticalScrollParent(element) {
    let parent = element ? element.parentElement : null;
    while (parent) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return document.scrollingElement || document.documentElement;
}

function scrollViewport(scrollParent) {
    if (scrollParent === document.scrollingElement || scrollParent === document.documentElement || scrollParent === document.body) {
        return { top: 0, bottom: window.innerHeight };
    }
    const rect = scrollParent.getBoundingClientRect();
    return { top: rect.top, bottom: rect.bottom };
}

function displayValue(value) {
    return value === null || value === undefined ? '' : value;
}

function SwatchCard({ item, disabled, dragging, selected, showImage, themestyle, onDelete }) {
    const detail = item.detail;
    const [hovered, setHovered] = useState(false);
    const labelStyle = {
        ...cardLabelStyle,
        backgroundColor: themestyle.header.back,
        color: themestyle.header.color
    };
    const valueStyle = {
        ...cardValueStyle,
        color: themestyle.body.color
    };

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                height: '100%',
                minWidth: 0,
                minHeight: 300,
                border: '1px solid #9E9E9E',
                outline: dragging ? '2px dashed #757D6C' : (selected ? '2px solid #757D6C' : 'none'),
                outlineOffset: dragging || selected ? -2 : 0,
                borderRadius: 3,
                backgroundColor: selected ? '#F0F1EC' : '#FFFFFF',
                boxSizing: 'border-box',
                userSelect: 'none',
                opacity: dragging ? 0.4 : 1,
                cursor: disabled ? 'default' : (dragging ? 'grabbing' : 'grab'),
                touchAction: disabled ? 'auto' : 'none',
                overflow: 'hidden'
            }}
        >
            <div style={{ borderBottom: '1px solid #BDBDBD' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '92px minmax(0, 1fr)', borderBottom: '1px solid #D6D6D6' }}>
                    <div style={labelStyle}>ブランド品番</div>
                    <div style={valueStyle} title={displayValue(detail.SWT_HINBAN)}>{displayValue(detail.SWT_HINBAN)}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '92px minmax(0, 1fr)' }}>
                    <div style={labelStyle}>PS品番</div>
                    <div style={valueStyle} title={displayValue(detail.SWT_OPPICD)}>{displayValue(detail.SWT_OPPICD)}</div>
                </div>
            </div>
            <div
                data-swatch-image-area="true"
                style={{ position: 'relative', height: 150, padding: 8, boxSizing: 'border-box', textAlign: 'center' }}
            >
                {hovered && !disabled && (
                    <IconButton
                        data-no-swatch-drag
                        aria-label="品番カードを削除"
                        size="small"
                        onPointerDown={event => event.stopPropagation()}
                        onClick={event => {
                            event.stopPropagation();
                            if (onDelete) onDelete(item.itemKey);
                        }}
                        style={{ position: 'absolute', top: 2, left: 2, color: '#757575', zIndex: 1 }}
                    >
                        <Icon style={{ fontSize: 22 }}>delete</Icon>
                    </IconButton>
                )}
                {showImage && detail.imageurl && (
                    <img
                        src={detail.imageurl}
                        alt=""
                        draggable={false}
                        style={{ maxWidth: '100%', height: '100%', objectFit: 'contain' }}
                    />
                )}
            </div>
            <div style={{ borderTop: '1px solid #BDBDBD' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '92px minmax(0, 1fr)', borderBottom: '1px solid #D6D6D6' }}>
                    <div style={labelStyle}>アイテム</div>
                    <div style={valueStyle} title={displayValue(detail.PDT_ITMCITEMCD_nm || detail.PDT_ITMCITEMCD)}>{displayValue(detail.PDT_ITMCITEMCD_nm || detail.PDT_ITMCITEMCD)}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '92px minmax(0, 1fr)', borderBottom: '1px solid #D6D6D6' }}>
                    <div style={labelStyle}>投入表No.</div>
                    <div style={valueStyle} title={displayValue(detail.SWT_TNYNO)}>{displayValue(detail.SWT_TNYNO)}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '92px minmax(0, 1fr)', borderBottom: '1px solid #D6D6D6' }}>
                    <div style={labelStyle}>企画番号</div>
                    <div style={valueStyle} title={displayValue(detail.PDT_DESIGNNO)}>{displayValue(detail.PDT_DESIGNNO)}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '46px minmax(0, 1fr) 32px minmax(0, 1fr)' }}>
                    <div style={labelStyle}>位置</div>
                    <div style={valueStyle} title={displayValue(detail.SWT_POSITIONNO)}>{displayValue(detail.SWT_POSITIONNO)}</div>
                    <div style={labelStyle}>週</div>
                    <div style={valueStyle} title={displayValue(detail.WeekNm)}>{displayValue(detail.WeekNm)}</div>
                </div>
            </div>
        </div>
    );
}

export default function SwatchBoard(props) {
    const themestyle = useSelector(state => state.form.themestyle);
    const boardRef = useRef(null);
    const ghostRef = useRef(null);
    const dragSessionRef = useRef(null);
    const autoScrollFrameRef = useRef(null);
    const boardDebugIdRef = useRef(null);
    const observedScrollRef = useRef(null);
    const pendingCardRectsRef = useRef(null);
    const cardAnimationsRef = useRef(new Map());
    if (boardDebugIdRef.current === null) {
        swatchBoardDebugIdSeed += 1;
        boardDebugIdRef.current = swatchBoardDebugIdSeed;
    }
    const [committedItems, setCommittedItems] = useState(props.items);
    const [previewItems, setPreviewItems] = useState(null);
    const [selectedItemKey, setSelectedItemKey] = useState(null);
    const [showImage, setShowImage] = useState(true);
    const [draggingKey, setDraggingKey] = useState(null);
    const [ghost, setGhost] = useState(null);
    const [gataSu, setGataSu] = useState(Number(props.gataSu));

    function changeSelection(nextItemKey) {
        const resolvedItemKey = typeof nextItemKey === 'function'
            ? nextItemKey(selectedItemKey)
            : nextItemKey;
        if (resolvedItemKey === selectedItemKey) return;
        setSelectedItemKey(resolvedItemKey);
        if (props.onSelectionChange) props.onSelectionChange(resolvedItemKey);
    }

    function stopAutoScroll() {
        if (autoScrollFrameRef.current !== null) {
            window.cancelAnimationFrame(autoScrollFrameRef.current);
            autoScrollFrameRef.current = null;
        }
    }

    function removeSessionListeners(session) {
        if (!session) return;
        window.removeEventListener('pointermove', session.handlePointerMove);
        window.removeEventListener('pointerup', session.handlePointerUp);
        window.removeEventListener('pointercancel', session.handlePointerCancel);
        window.removeEventListener('keydown', session.handleKeyDown);
        if (session.pointerTarget && session.pointerTarget.hasPointerCapture(session.pointerId)) {
            session.pointerTarget.releasePointerCapture(session.pointerId);
        }
    }

    function captureCardRects() {
        const rects = new Map();
        if (!boardRef.current) return rects;
        boardRef.current.querySelectorAll('[data-swatch-item-key]').forEach(element => {
            const itemKey = element.getAttribute('data-swatch-item-key');
            const rect = element.getBoundingClientRect();
            rects.set(itemKey, { left: rect.left, top: rect.top });
        });
        return rects;
    }

    function cardLayoutRect(element) {
        const rect = element.getBoundingClientRect();
        const transform = window.getComputedStyle(element).transform;
        let translateX = 0;
        let translateY = 0;
        if (transform && transform !== 'none') {
            const values = transform.substring(transform.indexOf('(') + 1, transform.length - 1)
                .split(',')
                .map(value => Number(value.trim()));
            if (transform.startsWith('matrix3d(')) {
                translateX = Number.isFinite(values[12]) ? values[12] : 0;
                translateY = Number.isFinite(values[13]) ? values[13] : 0;
            } else if (transform.startsWith('matrix(')) {
                translateX = Number.isFinite(values[4]) ? values[4] : 0;
                translateY = Number.isFinite(values[5]) ? values[5] : 0;
            }
        }
        return { left: rect.left - translateX, top: rect.top - translateY };
    }

    function cancelCardAnimations() {
        cardAnimationsRef.current.forEach(entry => entry.animation.cancel());
        cardAnimationsRef.current.clear();
    }

    function finishDrag(session) {
        pendingCardRectsRef.current = null;
        cancelCardAnimations();
        stopAutoScroll();
        removeSessionListeners(session);
        if (dragSessionRef.current === session) dragSessionRef.current = null;
        setPreviewItems(null);
        setDraggingKey(null);
        setGhost(null);
    }

    function moveGhost(session, clientX, clientY) {
        if (!ghostRef.current) return;
        const left = clientX - session.pointerOffsetX;
        const top = clientY - session.pointerOffsetY;
        ghostRef.current.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    }

    function chooseTargetPage(clientY) {
        if (!boardRef.current) return null;
        const pages = Array.from(boardRef.current.querySelectorAll('[data-swatch-page-no]'));
        let target = null;
        let nearestDistance = Number.POSITIVE_INFINITY;

        pages.forEach(page => {
            const rect = page.getBoundingClientRect();
            const distance = clientY < rect.top ? rect.top - clientY : (clientY > rect.bottom ? clientY - rect.bottom : 0);
            if (distance < nearestDistance) {
                target = page;
                nearestDistance = distance;
            }
        });

        return target;
    }

    function previewAtPoint(session, clientX, clientY) {
        const targetPage = chooseTargetPage(clientY);
        if (!targetPage) return;

        const targetPageNo = Number(targetPage.getAttribute('data-swatch-page-no'));
        if (session.activeTargetPageNo !== targetPageNo) {
            session.activeTargetPageNo = targetPageNo;
            session.horizontalDirection = 0;
        }
        const remainingItems = session.currentItems.filter(item => item.itemKey !== session.sourceItem.itemKey);
        const cardElements = Array.from(targetPage.querySelectorAll('[data-swatch-item-key]'))
            .filter(element => element.getAttribute('data-swatch-item-key') !== session.sourceItem.itemKey);
        let insertIndex = remainingItems.length;

        if (cardElements.length !== 0) {
            const boundaryRatio = session.horizontalDirection > 0 ? 0.25 : (session.horizontalDirection < 0 ? 0.75 : 0.5);
            const beforeElement = cardElements.find(element => {
                const rect = element.getBoundingClientRect();
                return clientX < rect.left + (rect.width * boundaryRatio);
            });

            if (beforeElement) {
                const beforeKey = beforeElement.getAttribute('data-swatch-item-key');
                insertIndex = remainingItems.findIndex(item => item.itemKey === beforeKey);
            } else {
                const lastKey = cardElements[cardElements.length - 1].getAttribute('data-swatch-item-key');
                const lastIndex = remainingItems.findIndex(item => item.itemKey === lastKey);
                if (lastIndex !== -1) insertIndex = lastIndex + 1;
            }
        } else {
            const nextPageIndex = remainingItems.findIndex(item => positiveNumber(item.detail.SWT_PAGENO, 1) > targetPageNo);
            if (nextPageIndex !== -1) insertIndex = nextPageIndex;
        }

        if (insertIndex < 0) return;
        const reorderedItems = [...remainingItems];
        reorderedItems.splice(insertIndex, 0, session.sourceItem);
        if (sameItemOrder(reorderedItems, session.currentItems)) return;

        pendingCardRectsRef.current = captureCardRects();
        const recalculatedItems = recalculatePreviewItems(reorderedItems, gataSu);
        session.currentItems = recalculatedItems;
        setPreviewItems(recalculatedItems);
    }

    function autoScrollStep() {
        autoScrollFrameRef.current = null;
        const session = dragSessionRef.current;
        if (!session || !session.dragging) return;

        const viewport = scrollViewport(session.scrollParent);
        let scrollAmount = 0;
        if (session.lastClientY < viewport.top + autoScrollEdge) {
            const ratio = Math.min(1, (viewport.top + autoScrollEdge - session.lastClientY) / autoScrollEdge);
            scrollAmount = -Math.ceil(autoScrollMaxSpeed * ratio);
        } else if (session.lastClientY > viewport.bottom - autoScrollEdge) {
            const ratio = Math.min(1, (session.lastClientY - (viewport.bottom - autoScrollEdge)) / autoScrollEdge);
            scrollAmount = Math.ceil(autoScrollMaxSpeed * ratio);
        }

        if (scrollAmount === 0) return;
        if (session.scrollParent === document.scrollingElement || session.scrollParent === document.documentElement || session.scrollParent === document.body) {
            window.scrollBy(0, scrollAmount);
        } else {
            session.scrollParent.scrollTop += scrollAmount;
        }
        previewAtPoint(session, session.lastClientX, session.lastClientY);
        autoScrollFrameRef.current = window.requestAnimationFrame(autoScrollStep);
    }

    function startAutoScroll() {
        if (autoScrollFrameRef.current === null) {
            autoScrollFrameRef.current = window.requestAnimationFrame(autoScrollStep);
        }
    }

    function startDrag(session, event) {
        session.dragging = true;
        session.scrollParent = nearestVerticalScrollParent(boardRef.current);
        const initialPreview = recalculatePreviewItems(session.originalItems, gataSu);
        session.currentItems = initialPreview;
        setPreviewItems(initialPreview);
        setDraggingKey(session.sourceItem.itemKey);
        setGhost({
            item: session.sourceItem,
            width: session.cardRect.width,
            height: session.cardRect.height,
            left: event.clientX - session.pointerOffsetX,
            top: event.clientY - session.pointerOffsetY
        });
    }

    function handlePointerDown(item, event) {
        if (props.disabled || dragSessionRef.current || event.button !== 0) return;
        if (event.target.closest('[data-no-swatch-drag]')) return;

        const card = event.currentTarget;
        const cardRect = card.getBoundingClientRect();
        const session = {
            pointerId: event.pointerId,
            pointerTarget: card,
            sourceItem: item,
            originalItems: committedItems,
            currentItems: committedItems,
            initialPageCount: pageCount(committedItems),
            activeTargetPageNo: positiveNumber(item.detail.SWT_PAGENO, 1),
            startX: event.clientX,
            startY: event.clientY,
            lastClientX: event.clientX,
            lastClientY: event.clientY,
            horizontalDirection: 0,
            pointerOffsetX: event.clientX - cardRect.left,
            pointerOffsetY: event.clientY - cardRect.top,
            cardRect: cardRect,
            dragging: false
        };

        session.handlePointerMove = pointerEvent => {
            if (pointerEvent.pointerId !== session.pointerId) return;
            if (pointerEvent.clientX > session.lastClientX) session.horizontalDirection = 1;
            if (pointerEvent.clientX < session.lastClientX) session.horizontalDirection = -1;
            session.lastClientX = pointerEvent.clientX;
            session.lastClientY = pointerEvent.clientY;

            if (!session.dragging) {
                const distance = Math.hypot(pointerEvent.clientX - session.startX, pointerEvent.clientY - session.startY);
                if (distance < dragStartThreshold) return;
                startDrag(session, pointerEvent);
            }

            pointerEvent.preventDefault();
            moveGhost(session, pointerEvent.clientX, pointerEvent.clientY);
            previewAtPoint(session, pointerEvent.clientX, pointerEvent.clientY);
            startAutoScroll();
        };
        session.handlePointerUp = pointerEvent => {
            if (pointerEvent.pointerId !== session.pointerId) return;
            const reorderedKeys = session.currentItems.map(value => value.itemKey);
            const changed = session.dragging && !sameItemOrder(session.currentItems, session.originalItems);
            const scrollTop = session.scrollParent ? session.scrollParent.scrollTop : null;
            console.log('[SwatchScroll] pointerup capture', {
                boardId: boardDebugIdRef.current,
                savedScrollTop: scrollTop,
                currentScrollTop: session.scrollParent ? session.scrollParent.scrollTop : null,
                tagName: session.scrollParent ? session.scrollParent.tagName : null,
                id: session.scrollParent ? session.scrollParent.id : null,
                className: session.scrollParent ? session.scrollParent.className : null,
                isConnected: session.scrollParent ? session.scrollParent.isConnected : null
            });
            if (!session.dragging) {
                changeSelection(currentKey => currentKey === session.sourceItem.itemKey ? null : session.sourceItem.itemKey);
            }
            finishDrag(session);
            if (changed) {
                const reorderedItems = props.onReorder(reorderedKeys, scrollTop);
                if (Array.isArray(reorderedItems)) setCommittedItems(reorderedItems);
            }
        };
        session.handlePointerCancel = pointerEvent => {
            if (pointerEvent.pointerId !== session.pointerId) return;
            finishDrag(session);
        };
        session.handleKeyDown = keyEvent => {
            if (keyEvent.key !== 'Escape') return;
            keyEvent.preventDefault();
            finishDrag(session);
        };

        dragSessionRef.current = session;
        card.setPointerCapture(event.pointerId);
        window.addEventListener('pointermove', session.handlePointerMove, { passive: false });
        window.addEventListener('pointerup', session.handlePointerUp);
        window.addEventListener('pointercancel', session.handlePointerCancel);
        window.addEventListener('keydown', session.handleKeyDown);
    }

    useEffect(() => {
        console.log('[SwatchScroll] board mount', {
            boardId: boardDebugIdRef.current
        });
        return () => {
            console.log('[SwatchScroll] board unmount', {
                boardId: boardDebugIdRef.current
            });
            removeObservedScrollListener(observedScrollRef);
            const session = dragSessionRef.current;
            if (autoScrollFrameRef.current !== null) window.cancelAnimationFrame(autoScrollFrameRef.current);
            removeSessionListeners(session);
            dragSessionRef.current = null;
            pendingCardRectsRef.current = null;
            cancelCardAnimations();
            if (props.onSelectionChange) props.onSelectionChange(null);
        };
    }, []);

    useEffect(() => {
        if (props.disabled && dragSessionRef.current) finishDrag(dragSessionRef.current);
    }, [props.disabled]);

    useEffect(() => {
        if (!dragSessionRef.current) setCommittedItems(props.items);
    }, [props.items]);

    useEffect(() => {
        if (!props.itemsSetterRef) return undefined;
        props.itemsSetterRef.current = items => {
            setPreviewItems(null);
            setCommittedItems(items);
            if (selectedItemKey !== null && !items.some(item => item.itemKey === selectedItemKey)) {
                setSelectedItemKey(null);
                if (props.onSelectionChange) props.onSelectionChange(null);
            }
        };
        return () => {
            props.itemsSetterRef.current = null;
        };
    }, [props.itemsSetterRef, props.onSelectionChange, selectedItemKey]);

    useEffect(() => {
        if (!props.showImageSetterRef) return undefined;
        props.showImageSetterRef.current = setShowImage;
        return () => {
            props.showImageSetterRef.current = null;
        };
    }, [props.showImageSetterRef]);

    useEffect(() => {
        if (!props.gataSuSetterRef) return undefined;
        props.gataSuSetterRef.current = setGataSu;
        return () => {
            props.gataSuSetterRef.current = null;
        };
    }, [props.gataSuSetterRef]);

    useLayoutEffect(() => {
        const previousRects = pendingCardRectsRef.current;
        pendingCardRectsRef.current = null;
        if (!previousRects) return;

        const session = dragSessionRef.current;
        if (!session || !session.dragging || !boardRef.current) return;

        boardRef.current.querySelectorAll('[data-swatch-item-key]').forEach(element => {
            const itemKey = element.getAttribute('data-swatch-item-key');
            if (itemKey === session.sourceItem.itemKey) return;
            const previousRect = previousRects.get(itemKey);
            if (!previousRect) return;

            const existingEntry = cardAnimationsRef.current.get(itemKey);
            if (existingEntry) {
                const targetRect = cardLayoutRect(element);
                const sameTarget = Math.abs(existingEntry.targetLeft - targetRect.left) < 0.5
                    && Math.abs(existingEntry.targetTop - targetRect.top) < 0.5;
                if (sameTarget) return;
                existingEntry.animation.cancel();
                if (cardAnimationsRef.current.get(itemKey) === existingEntry) {
                    cardAnimationsRef.current.delete(itemKey);
                }
            }

            const currentRect = element.getBoundingClientRect();
            const deltaX = previousRect.left - currentRect.left;
            const deltaY = previousRect.top - currentRect.top;
            if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) return;
            if (typeof element.animate !== 'function') return;

            const animation = element.animate(
                [
                    { transform: `translate3d(${deltaX}px, ${deltaY}px, 0)` },
                    { transform: 'translate3d(0, 0, 0)' }
                ],
                {
                    duration: 400,
                    easing: 'ease'
                }
            );
            cardAnimationsRef.current.set(itemKey, {
                animation,
                targetLeft: currentRect.left,
                targetTop: currentRect.top
            });
            const clearAnimation = () => {
                const currentEntry = cardAnimationsRef.current.get(itemKey);
                if (currentEntry && currentEntry.animation === animation) {
                    cardAnimationsRef.current.delete(itemKey);
                }
            };
            animation.onfinish = clearAnimation;
            animation.oncancel = clearAnimation;
        });
    }, [previewItems]);

    useLayoutEffect(() => {
        if (!props.pendingScrollTopRef) return;
        const scrollTop = props.pendingScrollTopRef.current;
        if (scrollTop === null) return;

        removeObservedScrollListener(observedScrollRef);
        let scrollParent = null;
        try {
            scrollParent = nearestVerticalScrollParent(boardRef.current);
            const scrollParentRect = scrollParent ? scrollParent.getBoundingClientRect() : null;
            console.log('[SwatchScroll] new board layoutEffect before restore', {
                boardId: boardDebugIdRef.current,
                savedScrollTop: scrollTop,
                beforeScrollTop: scrollParent ? scrollParent.scrollTop : null,
                tagName: scrollParent ? scrollParent.tagName : null,
                id: scrollParent ? scrollParent.id : null,
                className: scrollParent ? scrollParent.className : null,
                isConnected: scrollParent ? scrollParent.isConnected : null,
                scrollHeight: scrollParent ? scrollParent.scrollHeight : null,
                clientHeight: scrollParent ? scrollParent.clientHeight : null,
                top: scrollParentRect ? scrollParentRect.top : null,
                bottom: scrollParentRect ? scrollParentRect.bottom : null
            });
            if (scrollParent) scrollParent.scrollTop = scrollTop;
            console.log('[SwatchScroll] new board layoutEffect after restore', {
                boardId: boardDebugIdRef.current,
                savedScrollTop: scrollTop,
                afterScrollTop: scrollParent ? scrollParent.scrollTop : null
            });
            if (scrollParent) {
                const observation = {
                    element: scrollParent,
                    previousScrollTop: scrollParent.scrollTop,
                    startedAt: performance.now(),
                    listener: null
                };
                observation.listener = () => {
                    const currentScrollTop = observation.element.scrollTop;
                    const activeElement = document.activeElement;
                    console.log('[SwatchScroll] observed scroll change', {
                        boardId: boardDebugIdRef.current,
                        previousScrollTop: observation.previousScrollTop,
                        currentScrollTop: currentScrollTop,
                        elapsedMs: performance.now() - observation.startedAt,
                        activeElement: {
                            tagName: activeElement ? activeElement.tagName : null,
                            id: activeElement ? activeElement.id : null,
                            className: activeElement ? activeElement.className : null
                        },
                        isConnected: observation.element.isConnected,
                        scrollHeight: observation.element.scrollHeight,
                        clientHeight: observation.element.clientHeight
                    });
                    observation.previousScrollTop = currentScrollTop;
                };
                scrollParent.addEventListener('scroll', observation.listener);
                observedScrollRef.current = observation;
            }
            window.requestAnimationFrame(() => {
                console.log('[SwatchScroll] new board next frame', {
                    boardId: boardDebugIdRef.current,
                    scrollTop: scrollParent ? scrollParent.scrollTop : null,
                    isConnected: scrollParent ? scrollParent.isConnected : null
                });
            });
        } finally {
            props.pendingScrollTopRef.current = null;
        }
    }, [props.items, props.pendingScrollTopRef]);

    if (gataSu !== 6 && gataSu !== 8) {
        return <div style={{ padding: 8 }}>型数（6型または8型）を設定してください。</div>;
    }

    if (committedItems.length === 0) {
        return <div style={{ padding: 8 }}>表示する品番明細がありません。</div>;
    }

    const displayItems = previewItems || committedItems;
    const minimumPageCount = dragSessionRef.current && dragSessionRef.current.dragging
        ? dragSessionRef.current.initialPageCount
        : 1;
    const pages = groupItemsByPage(displayItems, minimumPageCount);

    return (
        <div style={{ flex: '1 1 auto', minWidth: 0, minHeight: 0, overflowX: 'auto', overflowY: 'auto' }}>
            <div ref={boardRef}>
            {pages.map(page => (
                <div
                    key={`page-${page.pageNo}`}
                    onPointerDown={event => {
                        if (!event.target.closest('[data-swatch-item-key]')) changeSelection(null);
                    }}
                    style={{ marginBottom: 12 }}
                >
                    <div style={{ marginBottom: 4, fontWeight: 700 }}>Page {page.pageNo}</div>
                    <div
                        data-swatch-page-no={page.pageNo}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${gataSu}, minmax(200px, 1fr))`,
                            gap: 4,
                            minWidth: (gataSu * 200) + ((gataSu - 1) * 4) + 8,
                            minHeight: 72,
                            padding: 4,
                            border: '1px solid #BDBDBD',
                            backgroundColor: '#F5F5F5',
                            boxSizing: 'border-box'
                        }}
                    >
                        {page.items.map(item => {
                            const blockCount = calculateSwatchBlockCount(item.detail.COLORCOUNT, item.detail.SIZECOUNT, gataSu);
                            const columnNo = positiveNumber(item.detail.SWT_COLUMNNO, 1);
                            const dragging = item.itemKey === draggingKey;
                            return (
                                <div
                                    key={item.itemKey}
                                    data-swatch-item-key={item.itemKey}
                                    onPointerDown={event => handlePointerDown(item, event)}
                                    onClick={props.disabled ? () => changeSelection(currentKey => currentKey === item.itemKey ? null : item.itemKey) : undefined}
                                    onDoubleClick={event => {
                                        if (!props.onImageDoubleClick) return;
                                        if (event.target.closest('[data-no-swatch-drag]')) return;
                                        const imageArea = event.currentTarget.querySelector('[data-swatch-image-area]');
                                        if (!imageArea) return;
                                        const rect = imageArea.getBoundingClientRect();
                                        const insideImageArea = event.clientX >= rect.left && event.clientX <= rect.right &&
                                            event.clientY >= rect.top && event.clientY <= rect.bottom;
                                        if (!insideImageArea) return;
                                        props.onImageDoubleClick(item.detail);
                                    }}
                                    style={{
                                        gridColumn: `${columnNo} / span ${blockCount}`,
                                        gridRow: 1,
                                        minWidth: 0
                                    }}
                                >
                                    <SwatchCard
                                        item={item}
                                        disabled={props.disabled}
                                        dragging={dragging}
                                        selected={item.itemKey === selectedItemKey}
                                        showImage={showImage}
                                        themestyle={themestyle}
                                        onDelete={props.onDelete}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
            {ghost && (
                <div
                    ref={ghostRef}
                    aria-hidden="true"
                    style={{
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        width: ghost.width,
                        height: ghost.height,
                        transform: `translate3d(${ghost.left}px, ${ghost.top}px, 0)`,
                        pointerEvents: 'none',
                        zIndex: 2000,
                        opacity: 0.85,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}
                >
                    <SwatchCard item={ghost.item} disabled={true} dragging={false} selected={false} showImage={showImage} themestyle={themestyle} />
                </div>
            )}
            </div>
        </div>
    );
}
